/**
 * ✅ PreApproveModal Component
 * Fecha: 30 de octubre de 2025
 * 
 * Modal para que el Project Manager pre-apruebe una solicitud de vacante.
 * Estado requerido: pendiente_validacion_pm → pre_aprobada
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from 'react'
import { X, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import type { JobRequest } from '@/services/api'
import { useJobRequestWorkflow } from '@/hooks/useJobRequestWorkflow'
import {
  formatJobRequestArea,
  formatJobRequestPriority,
  formatJobRequestSede
} from '@/features/rh/lib/jobRequestDisplay'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface PreApproveModalProps {
  jobRequest: JobRequest
  isOpen: boolean
  onClose: () => void
  onSuccess: (updated: JobRequest) => void
}

const PreApproveModal: React.FC<PreApproveModalProps> = ({
  jobRequest,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [comentarios, setComentarios] = useState('')
  const { preApprove, loading, error } = useJobRequestWorkflow()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const result = await preApprove(jobRequest.id, { comentariosPM: comentarios.trim() || undefined })

    if (result) {
      toast.success('Solicitud pre-aprobada exitosamente.')
      onSuccess(result)
      onClose()
      setComentarios('')
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
      setComentarios('')
    }
  }

  if (!isOpen) return null

  return (
    <CapaDeModal>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Pre-aprobar solicitud
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="bg-gray-600/5 w-[27px] h-[27px] flex justify-center items-center rounded-full text-gray-600/50 hover:text-gray-600 transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">

            {/* Información */}
            <div className="rounded-lg mb-8">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-[24px] h-[24px] text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm w-full">
                  <div className="w-full">
                    <p className="font-medium my-1 text-base">
                      Considerar al pre-aprobar:
                    </p>
                  </div>
                    <ul className="list-disc list-inside space-y-1 w-full flex justify-between flex-wrap">
                      <li className='min-w-1/2'>
                        La solicitud pasará a estado {' '}
                        <strong>"Pre-aprobada"</strong>
                      </li>
                      <li className='min-w-1/2'>RH podrá asignar los rangos salariales</li>
                      <li className='min-w-1/2'>Después podrás revisar y aprobar definitivamente</li>
                    </ul>
                </div>
              </div>
            </div>

            {/* Información de la solicitud */}
            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">
                {jobRequest.puestoSolicitar}
              </h3>
              <div className="text-sm space-y-1 flex justify-between mt-3">
                <p><strong className='text-[#777777] font-medium'>Departamento/Área:</strong><br /> {formatJobRequestArea(jobRequest.area?.nombre || jobRequest.areaVacante)}</p>
                <p><strong className='text-[#777777] font-medium'>Sede:</strong><br /> {formatJobRequestSede(jobRequest.sede)}</p>
                <p><strong className='text-[#777777] font-medium'>Prioridad:</strong><br /> {formatJobRequestPriority(jobRequest.prioridad)}</p>
              </div>
            </div>

            {/* Campo de comentarios */}
            <div>
              <label htmlFor="comentarios" className="block text-sm font-medium text-gray-700 mb-2">
                Comentarios (Opcional)
              </label>
              <textarea
                id="comentarios"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-[#F6F9FB] rounded-md bg-[#F6F9FB] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Agrega cualquier comentario o nota sobre esta pre-aprobación..."
                disabled={loading}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 bg-gray-50">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={estiloDeBoton('exito')}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Pre-aprobando...</span>
                </>
              ) : (
                <>
                  <span>Pre-aprobar solicitud</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  )
}

export default PreApproveModal
